import type { PlayerSim } from "./sim.ts";
import { Movement as M } from "./movement.ts";
import { areAllies } from "./teams.ts";

/**
 * Fighter actions run by the deterministic simulation (`stepPlayer`).
 * The values travel on the wire (`PlayerState.action`): never renumber them.
 */
export const Action = {
  None: 0,
  Light1: 1,
  Light2: 2,
  Light3: 3,
  /** Throws the carried object (B while carrying): the same move for every champion. */
  Throw: 4,
  SuperSlam: 5,
  /** Champion abilities (S1, S2): what they do is in each champion's moveset. */
  Special1: 6,
  Special2: 7,
  AirAttack: 8,
  DashAttack: 9,
} as const;
export type ActionId = (typeof Action)[keyof typeof Action];

/** Attack presses remembered by the simulation's input buffer (`PlayerSim.queued`). */
export const Press = {
  None: 0,
  Light: 1,
  /** B: picks up an object (the server decides) or throws the one carried. */
  Grab: 2,
  Slam: 3,
  Special1: 4,
  Special2: 5,
} as const;

/** Everything an attack does, as data (GDD §3.6). Times are in simulation steps (1/30 s). */
export interface AttackSpec {
  readonly id: ActionId;
  readonly name: string;
  /** Steps before the hitbox is live: the telegraph. */
  readonly startup: number;
  /** Steps the hitbox is live. */
  readonly active: number;
  /** Steps after the live window before the fighter is free again. */
  readonly recovery: number;
  /** Reach from the attacker's centre to the target's body, in metres. */
  readonly range: number;
  /** Width of the hitbox arc in degrees, centred on the facing (360 = all around). */
  readonly arcDeg: number;
  /** Horizontal speed given to the target (m/s). */
  readonly knockback: number;
  /** Vertical speed given to the target (m/s); 0 leaves it on the ground. */
  readonly lift: number;
  /** Steps the target loses control, before diminishing returns. */
  readonly hitstun: number;
  /** Slam meter gained by the attacker on hit, before the per-event cap. */
  readonly slamGain: number;
  /** Forward speed at the first step: a small step into the target (m/s, ground only). */
  readonly lunge: number;
  /** From this step a buffered press chains into `nextLight` / Super Slam. */
  readonly cancelFrom: number;
  readonly nextLight: ActionId;
  /** Whether a full meter can cancel this attack into a Super Slam. */
  readonly superCancel: boolean;
  /** Whether a buffered S1/S2 can cancel this attack (from `cancelFrom`), if the ability is off cooldown. */
  readonly specialCancel?: boolean;
  /**
   * Forward speed held from the first step to the end of the live window
   * (m/s): rushes and dash strikes. 0 = the fighter plants as usual.
   */
  readonly travel?: number;
  /** Leading steps during which normal attacks do not connect (Super Slams still do). */
  readonly armorTicks?: number;
  /** Steps before the move can be used again (abilities). At most 65535. */
  readonly cooldown?: number;
  /** Reject this ability while airborne. */
  readonly groundOnly?: boolean;
  /** Ranged move: the live window launches this projectile instead of a melee hitbox. */
  readonly projectile?: ProjectileSpec;
  /** A dive waits for landing before its damaging window begins. */
  readonly landingAttack?: boolean;
  /** On a successful aerial hit, bounce once per jump at this vertical speed. */
  readonly airBounce?: number;
  /** Normal attacks during this interval trigger the defensive response. */
  readonly counterFrom?: number;
  readonly counterUntil?: number;
  /** Sweep the victim sideways relative to the attacker's facing. */
  readonly sidePush?: boolean;
  /** HP removed in KO mode only; Slam mode keeps its existing rules. */
  readonly damage?: number;
}

/** A straight shot spawned by the server on the first live step of a ranged move. */
export interface ProjectileSpec {
  /** Height above the feet; omitted preserves the common chest-level launch. */
  readonly height?: number;
  /** Horizontal speed (m/s), along the caster's facing. */
  readonly speed: number;
  /** Steps before it fades. */
  readonly lifetime: number;
  /** Radius of the ball, in metres. */
  readonly radius: number;
  /**
   * Half-angle (degrees) of the cone in which the shot locks onto an enemy:
   * it then flies straight at them, up or down included (`aimTarget`).
   */
  readonly aimAssistDeg?: number;
  /**
   * Crawls on the floor at `height` above it (Cleopatra's asp): climbs low steps,
   * follows slopes, drops into the void off an edge; only real walls stop it.
   */
  readonly ground?: boolean;
}

export interface AttackDef extends Required<Omit<AttackSpec, "projectile">> {
  readonly projectile: ProjectileSpec | null;
  /** cos(arcDeg / 2), precomputed; -1 for a full circle. Used by the server only. */
  readonly arcCos: number;
}

/** Starting HP tuning; individual champion moves may override these values. */
const HP_DAMAGE: Record<ActionId, number> = {
  [Action.None]: 0, [Action.Light1]: 6, [Action.Light2]: 8, [Action.Light3]: 12,
  [Action.Throw]: 12, [Action.SuperSlam]: 32, [Action.Special1]: 12,
  [Action.Special2]: 6, [Action.AirAttack]: 10, [Action.DashAttack]: 10,
};

/** Fills the optional fields of an attack and precomputes its hitbox arc. */
export function attack(spec: AttackSpec): AttackDef {
  // Trigonometry is fine here: hit tests run on the server, outside `stepPlayer`.
  const arcCos = spec.arcDeg >= 360 ? -1 : Math.cos((spec.arcDeg * Math.PI) / 360);
  return Object.freeze({
    specialCancel: false,
    travel: 0,
    armorTicks: 0,
    cooldown: 0,
    groundOnly: false,
    landingAttack: false,
    airBounce: 0,
    counterFrom: 0,
    counterUntil: 0,
    sidePush: false,
    damage: HP_DAMAGE[spec.id],
    ...spec,
    projectile: spec.projectile ? Object.freeze({ ...spec.projectile }) : null,
    arcCos,
  });
}

/**
 * A champion's moves by slot. S1 and S2 may be missing (the button does
 * nothing); every fighter has the rest.
 */
export interface MoveSet {
  readonly light1: AttackDef;
  readonly light2: AttackDef;
  readonly light3: AttackDef;
  readonly special1: AttackDef | null;
  readonly special2: AttackDef | null;
  readonly superSlam: AttackDef;
  readonly airAttack?: AttackDef;
  readonly dashAttack?: AttackDef;
}

/** Moves indexed by `Action`, the way the simulation looks them up. */
export type MoveTable = readonly (AttackDef | undefined)[];

export function moveTable(moves: MoveSet): MoveTable {
  const table: (AttackDef | undefined)[] = [];
  table[Action.None] = undefined;
  table[Action.Light1] = moves.light1;
  table[Action.Light2] = moves.light2;
  table[Action.Light3] = moves.light3;
  table[Action.Throw] = THROW;
  table[Action.SuperSlam] = moves.superSlam;
  table[Action.Special1] = moves.special1 ?? undefined;
  table[Action.Special2] = moves.special2 ?? undefined;
  table[Action.AirAttack] = moves.airAttack;
  table[Action.DashAttack] = moves.dashAttack;
  return Object.freeze(table);
}

/**
 * Throwing the carried object (D39): the same timing for everyone; what the
 * shot does (speed, push) comes from the object, decided by the server.
 */
export const THROW: AttackDef = attack({
  id: Action.Throw, name: "Lancio",
  startup: 7, active: 1, recovery: 10,
  range: 0, arcDeg: 360, knockback: 9, lift: 3.5, hitstun: 22, slamGain: 7, lunge: 0,
  cancelFrom: 255, nextLight: Action.None, superCancel: false,
  projectile: { speed: 15, lifetime: 20, radius: 0.45 },
});

/**
 * Standard move list (the Phase 1 fighter, without abilities). A combo is
 * A → A → A (the third hit launches), and any attack can be cancelled into
 * the Super Slam while the meter is full. B picks up and throws objects
 * (D39). The champions in `@arena/character-data` start from these and
 * change what they need.
 */
export const ATTACKS = {
  light1: attack({
    id: Action.Light1, name: "Diretto",
    startup: 3, active: 2, recovery: 7,
    range: 1.1, arcDeg: 110, knockback: 2.5, lift: 0, hitstun: 13, slamGain: 5, lunge: 3.5,
    cancelFrom: 5, nextLight: Action.Light2, superCancel: true,
  }),
  light2: attack({
    id: Action.Light2, name: "Gancio",
    startup: 3, active: 2, recovery: 8,
    range: 1.1, arcDeg: 120, knockback: 3, lift: 0, hitstun: 14, slamGain: 5, lunge: 3.5,
    cancelFrom: 5, nextLight: Action.Light3, superCancel: true,
  }),
  light3: attack({
    id: Action.Light3, name: "Montante",
    startup: 5, active: 2, recovery: 12,
    range: 1.25, arcDeg: 120, knockback: 8.5, lift: 5, hitstun: 22, slamGain: 10, lunge: 5,
    cancelFrom: 8, nextLight: Action.None, superCancel: true,
  }),
  superSlam: attack({
    id: Action.SuperSlam, name: "Super Slam",
    startup: 12, active: 1, recovery: 22,
    range: 3.2, arcDeg: 360, knockback: 13, lift: 8.5, hitstun: 32, slamGain: 0, lunge: 0,
    cancelFrom: 255, nextLight: Action.None, superCancel: false,
    armorTicks: 12,
  }),
} as const satisfies Record<string, AttackDef>;

export const STANDARD_MOVES: MoveSet = Object.freeze({ ...ATTACKS, special1: null, special2: null });

/** The move a fighter is performing, looked up in its champion's table. */
export function attackDef(moves: MoveTable, action: number): AttackDef | undefined {
  return moves[action];
}

/** A successful counter strengthens this use only; the manifest remains unchanged. */
export function effectiveAttack(moves: MoveTable, state: Pick<PlayerSim, "action" | "countered">): AttackDef | undefined {
  const def=moves[state.action];
  return def && state.countered && def.counterUntil>0
    ? { ...def, range:def.range*1.3, knockback:def.knockback*1.8, lift:def.lift+2, hitstun:def.hitstun+6, damage:def.damage*1.5 }
    : def;
}

export function canCounter(state: Pick<PlayerSim,"action"|"actionTick"|"countered"|"grounded">, moves:MoveTable): boolean {
  const def=moves[state.action];
  return !!def && state.grounded && !state.countered && def.counterUntil>0 && state.actionTick>=def.counterFrom && state.actionTick<def.counterUntil;
}

/** Combat tuning shared by the simulation and the server. */
export const Combat = {
  /**
   * Wind-up of every champion's Super Slam: the same telegraph across the
   * roster, so a defender reads it without knowing the champion (D22).
   */
  superStartup: 12,
  /** Steps an attack press stays buffered while the fighter cannot act yet. */
  pressBufferSteps: 8,
  /** Ground deceleration while attacking (m/s²): attacks plant the fighter. */
  actionDecel: 45,
  /** Ground friction while in hitstun (m/s²): how far knockback slides. */
  knockbackFriction: 22,
  maxKnockback: 16,
  /** Leading dash steps with invulnerability (GDD §3.4, Dodge). */
  dashInvulnSteps: 4,
  /** Hitstun multiplier by hits already taken in the combo: diminishing returns against stunlock. */
  hitstunScale: [1, 1, 0.9, 0.75, 0.6, 0.5, 0.4],
  /** Extra knockback per hit already taken in the combo: long combos push the target out of reach. */
  knockbackGrowth: 0.12,
  minHitstun: 4,
  /**
   * Radius of the body push between fighters, smaller than the drawn body
   * (`Movement.radius`): shoulders may touch, but fighters trading blows at
   * attack range rarely push each other — and each push is a correction the
   * clients cannot predict (docs/DECISIONS.md, D18).
   */
  pushRadius: 0.35,
  /** Top speed while carrying an object, as a share of the normal one. */
  carrySpeed: 0.75,
  /** Top speed on Gandhi's salt, as a share of the normal one (slowed by 70%). */
  saltSpeed: 0.3,
  /** Top speed in Maria Antonietta's whirlwind, as a share of the normal one. */
  spinSpeed: 1.5,
} as const;

export function actionLength(def: AttackDef): number {
  return def.startup + def.active + def.recovery;
}

export type AttackPhase = "startup" | "active" | "recovery";

export function attackPhase(def: AttackDef, tick: number): AttackPhase {
  if (tick < def.startup) return "startup";
  if (tick < def.startup + def.active) return "active";
  return "recovery";
}

export function isActiveTick(def: AttackDef, tick: number): boolean {
  return tick >= def.startup && tick < def.startup + def.active;
}

/** Inside the leading invulnerable steps of a dash (the dodge). */
export function isDodging(s: Pick<PlayerSim, "dashTicks">): boolean {
  return s.dashTicks > M.dashTicks - 1 - Combat.dashInvulnSteps;
}

/** Winding up a Super Slam: normal attacks do not interrupt it, other Super Slams do. */
export function isSuperStartup(s: Pick<PlayerSim, "action" | "actionTick">): boolean {
  return s.action === Action.SuperSlam && s.actionTick < Combat.superStartup;
}

/** Inside the armoured opening of the current move (Super Slam wind-up, a rush…). */
export function isArmored(s: Pick<PlayerSim, "action" | "actionTick">, moves: MoveTable): boolean {
  const move = moves[s.action];
  return move !== undefined && s.actionTick < move.armorTicks && (move.counterUntil===0 || s.actionTick>=move.counterFrom);
}

/**
 * Whether `target` can be hit by `def` right now: a dodge avoids everything,
 * armour (looked up in `targetMoves`, the target's own champion) only normal attacks.
 */
export function canBeHit(
  target: Pick<PlayerSim, "dashTicks" | "action" | "actionTick">,
  targetMoves: MoveTable,
  def: AttackDef,
): boolean {
  if (isDodging(target)) return false;
  return def.id === Action.SuperSlam || !isArmored(target, targetMoves);
}

export interface Placement {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface Facing extends Placement {
  readonly fx: number;
  readonly fz: number;
}

/**
 * Hitbox test: a horizontal sector of radius `range` (plus the target's body
 * radius) in front of the attacker, against the target's cylinder.
 */
export function inHitbox(attacker: Facing, def: AttackDef, target: Placement): boolean {
  const dy = target.y - attacker.y;
  if (dy >= M.height || dy <= -M.height) return false;
  const dx = target.x - attacker.x;
  const dz = target.z - attacker.z;
  const reach = def.range + M.radius;
  const d2 = dx * dx + dz * dz;
  if (d2 > reach * reach) return false;
  if (def.arcCos <= -1) return true;
  const d = Math.sqrt(d2);
  if (d <= M.radius) return true; // bodies overlapping: always in front enough
  return dx * attacker.fx + dz * attacker.fz >= def.arcCos * d;
}

/** How a champion takes hits (from its weight and resistance, see `deriveKit`). */
export interface HitResponse {
  /** Multiplier on the knockback received. */
  readonly knockbackTaken: number;
  /** Multiplier on the hitstun received. */
  readonly hitstunTaken: number;
  /** Multiplier on the damage received in Arena KO (Maria Antonietta's wig); 1 when absent. */
  readonly damageTaken?: number;
}

export const NEUTRAL_RESPONSE: HitResponse = Object.freeze({ knockbackTaken: 1, hitstunTaken: 1 });

/**
 * Puts `victim` in hitstun with knockback away from `source`. `comboHits` is
 * how many hits it already took without regaining control: each one shortens
 * the stun and lengthens the push (diminishing returns, GDD §3.4). Heavy
 * champions fly less far, resistant ones recover sooner (`response`).
 */
export function applyHit(
  victim: PlayerSim,
  source: Facing,
  def: AttackDef,
  comboHits: number,
  response: HitResponse = NEUTRAL_RESPONSE,
): void {
  let dx = victim.x - source.x;
  let dz = victim.z - source.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d < 1e-4) {
    dx = source.fx;
    dz = source.fz;
  } else {
    dx /= d;
    dz /= d;
  }

  if(def.sidePush) {
    const side=dx*source.fz-dz*source.fx>=0?1:-1;
    dx=source.fz*side; dz=-source.fx*side;
  }
  const scales = Combat.hitstunScale;
  const scale = scales[Math.min(comboHits, scales.length - 1)] ?? 1;
  victim.hitstun = Math.min(255, Math.max(Combat.minHitstun, Math.round(def.hitstun * scale * response.hitstunTaken)));
  const speed = Math.min(
    def.knockback * (1 + Combat.knockbackGrowth * comboHits) * response.knockbackTaken,
    Combat.maxKnockback,
  );
  victim.vx = Math.fround(dx * speed);
  victim.vz = Math.fround(dz * speed);
  if (def.lift > 0) {
    victim.vy = Math.fround(def.lift);
    victim.grounded = false;
    victim.airTicks = M.coyoteTicks + 1; // no coyote jump out of a launch
  }
  victim.fx = Math.fround(-dx);
  victim.fz = Math.fround(-dz);
  victim.action = Action.None;
  victim.actionTick = 0;
  victim.countered = false;
  victim.queued = Press.None;
  victim.queueTicks = 0;
  victim.dashTicks = 0;
  if(victim.anchorTicks>0){victim.vx=victim.vz=0;victim.vy=victim.grounded?0:Math.min(victim.vy,-18);victim.hitstun=Math.max(victim.hitstun,victim.anchorTicks);}
}

export interface SlamRules {
  readonly slamMax: number;
  readonly maxSlamGainPerEvent: number;
  readonly victimSlamFactor: number;
}

/**
 * Slam meter earned by one hit (GDD §3.3): the attacker gains the attack's
 * value times its champion's charge rate, capped per event; the victim gains
 * a fraction ("pressure"), so the player being comboed also gets closer to a
 * comeback.
 */
export function slamGain(def: AttackDef, rules: SlamRules, chargeRate = 1): { attacker: number; victim: number } {
  const attacker = Math.max(0, Math.min(Math.round(def.slamGain * chargeRate), rules.maxSlamGainPerEvent));
  return { attacker, victim: Math.floor(attacker * rules.victimSlamFactor) };
}

export function addSlam(meter: number, gain: number, rules: SlamRules): number {
  return Math.min(rules.slamMax, meter + gain);
}

export interface SlamCaster extends Facing {
  readonly id: string;
  /** The caster's own Super Slam (shapes differ between champions); the standard one if omitted. */
  readonly def?: AttackDef;
  /** `Team` of the caster; allies are never hit (D27). */
  readonly team?: number;
}

export interface SlamCandidate extends Placement {
  readonly id: string;
  /** Anti-chain protection or dodge: Super Slams pass through. */
  readonly immune: boolean;
  readonly team?: number;
}

export interface SlamOutcome {
  readonly casterId: string;
  /** Each target at most once: one point per target per Super Slam. */
  readonly targets: readonly string[];
}

/**
 * Resolves every Super Slam that goes off in the same server tick against one
 * snapshot of the world (GDD §3.7 and §13.3 "Super Slam simultanee"): each
 * outcome depends only on its caster and the snapshot, so the order of
 * `casters` never changes the result. Casters can hit each other; nobody
 * hits themselves; immune candidates are skipped.
 */
export function resolveSuperSlams(
  casters: readonly SlamCaster[],
  candidates: readonly SlamCandidate[],
): SlamOutcome[] {
  return casters.map((caster) => ({
    casterId: caster.id,
    targets: candidates
      .filter((c) => c.id !== caster.id && !c.immune && !areAllies(caster.team ?? 0, c.team ?? 0) && inHitbox(caster, caster.def ?? ATTACKS.superSlam, c))
      .map((c) => c.id),
  }));
}

/** Pushes overlapping fighters apart on the XZ plane, half each (server only). */
export function separateFighters(fighters: readonly PlayerSim[]): PlayerSim[] {
  const moved = new Set<PlayerSim>();
  const minDist = Combat.pushRadius * 2;
  for (let i = 0; i < fighters.length; i++) {
    for (let j = i + 1; j < fighters.length; j++) {
      const a = fighters[i]!;
      const b = fighters[j]!;
      // Dashing fighters pass through each other (cross-ups); stacked ones do not touch.
      if (a.dashTicks > 0 || b.dashTicks > 0) continue;
      if (a.y >= b.y + M.height || b.y >= a.y + M.height) continue;
      let nx = b.x - a.x;
      let nz = b.z - a.z;
      const d2 = nx * nx + nz * nz;
      if (d2 >= minDist * minDist) continue;
      let d = Math.sqrt(d2);
      if (d < 1e-4) {
        nx = 1;
        nz = 0;
        d = 0;
      } else {
        nx /= d;
        nz /= d;
      }
      if(a.anchorTicks>0 && b.anchorTicks>0)continue;
      const push = (minDist - d) / 2;
      const pushA=a.anchorTicks>0?0:b.anchorTicks>0?push*2:push;
      const pushB=b.anchorTicks>0?0:a.anchorTicks>0?push*2:push;
      a.x = Math.fround(a.x - nx * pushA);
      a.z = Math.fround(a.z - nz * pushA);
      b.x = Math.fround(b.x + nx * pushB);
      b.z = Math.fround(b.z + nz * pushB);
      moved.add(a);
      moved.add(b);
    }
  }
  return [...moved];
}
